import { CalendarDays } from "lucide-react";
import { type FC, type ReactNode, useState } from "react";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

import {
  deadlineUrgency,
  formatDeadline,
  isoAtNoonUtc,
  relativeDeadline
} from "../caseHeaderModel";
import { Chip } from "./chip";

const URGENCY_CLASS: Record<ReturnType<typeof deadlineUrgency>, string> = {
  none: "text-muted-foreground",
  ok: "",
  soon: "border-warn-line bg-warn-soft text-warn hover:bg-warn-soft",
  overdue: "border-danger-line bg-danger-soft text-danger hover:bg-danger-soft"
};

/**
 * Calendar popover for the filing deadline. `children` is the trigger subtree
 * and must contain a `<PopoverTrigger asChild>` (so it can be nested inside a
 * Tooltip trigger when needed).
 */
export const DeadlinePopover: FC<{
  value: string | null;
  onChange: (iso: string | null) => void | Promise<void>;
  align?: "start" | "end" | "center";
  children: ReactNode;
}> = ({ value, onChange, align = "end", children }) => {
  const [open, setOpen] = useState(false);
  const selected = value ? new Date(value) : undefined;
  const valid =
    selected && !Number.isNaN(selected.getTime()) ? selected : undefined;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      {children}
      <PopoverContent align={align} className="w-auto p-2">
        <Calendar
          mode="single"
          selected={valid}
          defaultMonth={valid}
          onSelect={(date) => {
            if (!date) return;
            void onChange(isoAtNoonUtc(date));
            setOpen(false);
          }}
        />
        <div className="flex items-center justify-between border-t border-border pt-2">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs"
            onClick={() => {
              void onChange(isoAtNoonUtc(new Date()));
              setOpen(false);
            }}
          >
            Today
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs text-muted-foreground"
            disabled={!value}
            onClick={() => {
              void onChange(null);
              setOpen(false);
            }}
          >
            Clear
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export const deadlineTooltip = (value: string | null): string => {
  const label = formatDeadline(value);
  if (!label) return "Set a filing deadline";
  const urgency = deadlineUrgency(value);
  return `Filing deadline ${relativeDeadline(value)}${
    urgency === "overdue" ? " (overdue)" : ""
  }`;
};

export const DeadlineChip: FC<{
  value: string | null;
  onChange: (iso: string | null) => void | Promise<void>;
  className?: string;
}> = ({ value, onChange, className }) => {
  const urgency = deadlineUrgency(value);
  const label = formatDeadline(value);
  return (
    <DeadlinePopover value={value} onChange={onChange}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Chip
              aria-label={
                label ? `Filing deadline ${label}` : "Set filing deadline"
              }
              className={cn(URGENCY_CLASS[urgency], className)}
            >
              <CalendarDays
                className={cn(
                  "size-3.5 shrink-0",
                  urgency === "soon" || urgency === "overdue"
                    ? "opacity-70"
                    : "text-muted-foreground"
                )}
              />
              <span className="truncate">{label || "No deadline"}</span>
            </Chip>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side="bottom">{deadlineTooltip(value)}</TooltipContent>
      </Tooltip>
    </DeadlinePopover>
  );
};
